/** W82 Beta-W (entscheide C5): das Bildschirmfoto wird im Browser verkleinert, bevor es geht. Lange Seite höchstens 2048 px,
 *  neu als JPEG 0,85 gezeichnet; das Neuzeichnen nimmt Metadaten (Ort, Gerät, Exif) mit Sicherheit weg und hält das Bild meist
 *  unter 1 MB, damit die Grenze von 30 s auch über schwaches Netz reicht. Ohne lesbares Bild `BildFehler` „unlesbar“ (RM34),
 *  über 5 MB nach dem Verkleinern „gross“ (RM32, ohne Anfrage). Chromium liest HEIC nicht (RM34); Safari liefert HEIC aus der
 *  Mediathek bei `accept="image/png,image/jpeg"` schon als JPEG. */
export const LANGE_SEITE = 2048;
export const JPEG_GUETE = 0.85;
/** Grenze des Servers (`BILD_MAX` 5.242.880 Bytes). */
export const BILD_MAX = 5 * 1024 * 1024;

export class BildFehler extends Error {
  readonly art: "unlesbar" | "gross";
  constructor(art: "unlesbar" | "gross") {
    super(art);
    this.art = art;
  }
}

type Quelle = { bild: CanvasImageSource; breite: number; hoehe: number; fertig: () => void };

/** Breite und Höhe nach dem Verkleinern: die lange Seite höchstens `LANGE_SEITE`, nie größer als das Original. */
export function zielMass(breite: number, hoehe: number): { breite: number; hoehe: number } {
  const lang = Math.max(breite, hoehe);
  const faktor = lang > LANGE_SEITE ? LANGE_SEITE / lang : 1;
  return { breite: Math.max(1, Math.round(breite * faktor)), hoehe: Math.max(1, Math.round(hoehe * faktor)) };
}

async function lesen(datei: Blob): Promise<Quelle> {
  if (typeof createImageBitmap === "function") {
    try {
      const b = await createImageBitmap(datei);
      return { bild: b, breite: b.width, hoehe: b.height, fertig: () => b.close() };
    } catch {
      // Rückfall unten: manche Browser lesen über <img> mehr Formate als über createImageBitmap
    }
  }
  const url = URL.createObjectURL(datei);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return { bild: img, breite: img.naturalWidth, hoehe: img.naturalHeight, fertig: () => URL.revokeObjectURL(url) };
  } catch {
    URL.revokeObjectURL(url);
    throw new BildFehler("unlesbar");
  }
}

export async function bildVerkleinern(datei: File | Blob): Promise<Blob> {
  const quelle = await lesen(datei);
  try {
    if (!quelle.breite || !quelle.hoehe) throw new BildFehler("unlesbar");
    const mass = zielMass(quelle.breite, quelle.hoehe);
    const flaeche = document.createElement("canvas");
    flaeche.width = mass.breite;
    flaeche.height = mass.hoehe;
    const stift = flaeche.getContext("2d");
    if (!stift) throw new BildFehler("unlesbar");
    // durchsichtige Stellen eines PNG würden im JPEG schwarz
    stift.fillStyle = "white";
    stift.fillRect(0, 0, mass.breite, mass.hoehe);
    stift.drawImage(quelle.bild, 0, 0, mass.breite, mass.hoehe);
    const blob = await new Promise<Blob | null>((fertig) => flaeche.toBlob(fertig, "image/jpeg", JPEG_GUETE));
    if (!blob || blob.size === 0) throw new BildFehler("unlesbar");
    if (blob.size > BILD_MAX) throw new BildFehler("gross");
    return blob;
  } finally {
    quelle.fertig();
  }
}
